import { formatDateTime } from '../dates';
import {
  complianceCoverageText,
  complianceIssueBreakdown,
  presentComplianceTruth,
  dashboardComplianceTone,
  type ComplianceTruthProjection,
} from '../presentation/compliance-truth';
import { StatusBadge } from './StatusBadge';

export type ComplianceTruthBannerProps = Readonly<{
  truth?: ComplianceTruthProjection | null | undefined;
  heading?: string;
  compact?: boolean;
  discloseDates?: boolean;
  dashboard?: boolean;
}>;

export function ComplianceTruthBanner({
  truth,
  heading = 'Compliance status',
  compact = false,
  discloseDates = false,
  dashboard = false,
}: ComplianceTruthBannerProps) {
  const presentation = presentComplianceTruth(truth?.state);
  const secondary = (truth?.secondary_states ?? [])
    .filter((state) => state !== presentation.state)
    .map((state) => presentComplianceTruth(state).label);
  const setupLabels=(truth?.secondary_states??[]).filter(state=>state!==presentation.state&&['not_configured','setup_incomplete','information_incomplete'].includes(state)).map(state=>presentComplianceTruth(state).label);
  const otherLabels=secondary.filter(label=>!setupLabels.includes(label));
  const hasDates = Boolean(truth?.last_reliable_assessment_at || truth?.last_required_monitoring_activity_at || truth?.calculated_at);
  const dates = <>
    {truth?.last_reliable_assessment_at && <div><dt>Assessed</dt><dd>{formatDateTime(truth.last_reliable_assessment_at)}</dd></div>}
    {truth?.last_required_monitoring_activity_at && <div><dt>Monitoring activity</dt><dd>{formatDateTime(truth.last_required_monitoring_activity_at)}</dd></div>}
    {truth?.calculated_at && <div><dt>Status calculated</dt><dd>{formatDateTime(truth.calculated_at)}</dd></div>}
  </>;

  if (dashboard) return <section className="compliance-truth compliance-truth--dashboard" aria-label={heading} data-compliance-state={presentation.state} data-dashboard-tone={dashboardComplianceTone(truth)}>
    <div className="compliance-truth__position"><div className="compliance-truth__heading"><h2>{heading}</h2><StatusBadge text={presentation.label} tone={dashboardComplianceTone(truth)}/></div>
    {truth?.issues.total ? <p className="dashboard-issue-count"><strong>{truth.issues.total} compliance {truth.issues.total === 1 ? 'issue' : 'issues'}</strong>{truth.issues.highest_severity ? ` · highest severity ${truth.issues.highest_severity}` : ''}</p> : null}
    {complianceIssueBreakdown(truth) ? <p className="compliance-truth__secondary">{complianceIssueBreakdown(truth)}</p> : null}
    </div>
    {setupLabels.length>0&&<div className="dashboard-setup-note"><strong>Setup information</strong><p>{setupLabels.join(' · ')}</p></div>}
    {otherLabels.length>0&&<p className="compliance-truth__secondary">{otherLabels.join(' · ')}</p>}
    <details className="dashboard-assessment-detail"><summary>Assessment details</summary>
      <p className="compliance-truth__meaning">{presentation.description}</p>
      <dl className="compliance-truth__facts"><div><dt>Coverage</dt><dd>{complianceCoverageText(truth)}</dd></div>{dates}</dl>
      <p className="compliance-truth__disclaimer">{truth?.disclaimer ?? 'Operational indication — not legal certification.'}</p>
    </details>
  </section>;

  return <section
    className={`compliance-truth${compact ? ' compliance-truth--compact' : ''}`}
    aria-label={heading}
    data-compliance-state={presentation.state}
  >
    <div className="compliance-truth__heading">
      <div>
        <span className="eyebrow">Operational indication</span>
        <h2>{heading}</h2>
      </div>
      <StatusBadge text={presentation.label} tone={presentation.tone} />
    </div>
    <p className="compliance-truth__meaning">{presentation.description}</p>
    {secondary.length > 0 && <p className="compliance-truth__secondary"><strong>Also affecting this status:</strong> {secondary.join(' · ')}</p>}
    <dl className="compliance-truth__facts">
      {truth?.issues.total ? <div><dt>Known issues</dt><dd>{truth.issues.total}{truth.issues.highest_severity ? ` · highest severity ${truth.issues.highest_severity}` : ''}{complianceIssueBreakdown(truth) ? <><br/>{complianceIssueBreakdown(truth)}</> : null}</dd></div> : null}
      <div><dt>Coverage</dt><dd>{complianceCoverageText(truth)}</dd></div>
      {!discloseDates && dates}
    </dl>
    {discloseDates && hasDates && <details className="compliance-truth__dates"><summary>Assessment dates</summary><dl className="compliance-truth__facts">{dates}</dl></details>}
    <p className="compliance-truth__disclaimer">{truth?.disclaimer ?? 'Operational indication — not legal certification.'}</p>
  </section>;
}
